// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {ExperimentalTooltipEl, TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';

import type {Toggle} from './index';
import {NarrowMode, ToggleSize, ToggleTheme} from './types';

function IconContainer(attrs) {
  const {icon} = attrs;
  return (
    <div sel='.icon-container' attrs={{icon}}>
      <SvgIconEl
        attrs={{
          icon,
        }}
      />
    </div>
  );
}

export function template(this: Toggle) {
  const options = this.helpers.toggleOptions();
  const {narrowMode} = this.state;
  const selectedIndex = (options || []).findIndex((toggleOption) => toggleOption.value === this.attr(`selected`));
  const theme = this.attr(`theme`);
  const hideTooltip = theme === `tab-bar-heavy` && narrowMode !== NarrowMode.OnlyIcons;
  const {print} = this.getContext(`i18n`);

  return (
    <div
      sel='.mp-toggle'
      class={{
        'toggle-large': this.attr(`size`) === ToggleSize.Large,
        'toggle-small': this.attr(`size`) === ToggleSize.Small,
        'toggle-inline': Boolean(this.attr(`inline`)),
        'theme-tabbar': theme === ToggleTheme.TabBar,
        'theme-tabbar-borderless': theme === ToggleTheme.TabBarBorderless,
        'theme-tabbar-lite': theme === ToggleTheme.TabBarLite,
        'theme-tabbar-heavy': theme === ToggleTheme.TabBarHeavy,
        'theme-segcon': theme === ToggleTheme.SegmentedController,
        'theme-large-font': theme === ToggleTheme.TabBarLargeFont,
      }}
      hook={{insert: (vnode) => this.wrapperInserted(vnode)}}
    >
      {options.map((toggleOption, index) => {
        const isSelected = index === selectedIndex;
        const beforeSelected = index === selectedIndex - 1;
        const {icon, iconColor, textColor, value, disabled, tooltip, tooltipLabel, href} = toggleOption;
        const label = print(toggleOption.label);
        const showLabel = label.length && narrowMode !== NarrowMode.OnlyIcons;
        const hasTextAndIcon = Boolean(icon && showLabel);

        let tooltipEl: VNode = null;
        if (tooltip) {
          tooltipEl = <TooltipEl attrs={tooltip} />;
        } else if (tooltipLabel) {
          tooltipEl = <TooltipEl attrs={{placement: Placement.Top, text: tooltipLabel}} />;
        }

        return theme === `segmented-controller` ? (
          <div sel='.mp-toggle-option' class={{selected: isSelected}}>
            <ButtonEl
              sel='.mp-toggle-button'
              attrs={{
                theme: ButtonTheme.Tertiary,
                // @ts-ignore Type 'ToggleSize' is not assignable to type 'ButtonSize'.
                size: this.attr(`size`),
                icon,
                disabled,
                selected: isSelected,
                'not-focusable': true,
                href,
              }}
              on={{
                click: () => !disabled && this.helpers.selectOption(value),
              }}
            >
              {label}
            </ButtonEl>
            {tooltipEl}
          </div>
        ) : (
          <a
            sel='.mp-toggle-option'
            class={{
              'mp-toggle-selected': isSelected,
              'before-selected': beforeSelected,
              'mp-toggle-disabled': disabled,
              'mp-toggle-text-and-icon': hasTextAndIcon,
              'has-label': showLabel,
            }}
            style={{
              '--selected-toggle-icon-color': iconColor,
              '--selected-toggle-text-color': textColor,
            }}
            on={{
              click: () => !disabled && this.helpers.selectOption(value),
              mouseenter: () => this.hovered(value),
              mouseleave: () => this.cancelHover(),
            }}
            attrs={{
              ...(href ? {href} : {}),
              role: `tab`,
            }}
          >
            <div sel='.container'>
              {icon ? <IconContainer icon={icon} /> : null}
              {showLabel ? label : null}
            </div>
            <div sel='.highlight-bar' />
            {!hideTooltip && tooltipLabel ? (
              <ExperimentalTooltipEl attrs={{placement: Placement.Top, text: tooltipLabel}} />
            ) : null}
          </a>
        );
      })}
    </div>
  );
}
